import React, { useState, useEffect } from 'react';
import { Search, MapPin, Home, DollarSign, BedDouble, X, ArrowDownUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { properties } from '@/data/properties';

const QuickSearch = ({ onSearch }) => {
    const [filters, setFilters] = useState({
        zone: 'all',
        type: 'all',
        category: 'all',
        bedrooms: 'all'
    });

    const [sortOrder, setSortOrder] = useState('default'); // default, price-asc, price-desc, recent

    const [options, setOptions] = useState({
        zones: [],
        types: [],
        categories: [],
        bedrooms: []
    });

    useEffect(() => {
        const zones = [...new Set(properties.map(p => p.ubicacion.zona))].sort();
        const types = [...new Set(properties.map(p => p.operacion))].sort(); // Was type (Venta/Alquiler) -> operacion
        const categories = [...new Set(properties.map(p => p.tipo))].sort(); // Was category (Casa/Apto) -> tipo
        const bedrooms = [...new Set(properties.map(p => p.dimensiones.habitaciones))].sort((a, b) => a - b);

        setOptions({ zones, types, categories, bedrooms });
    }, []);

    const handleFilterChange = (key, value) => {
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);
        if (onSearch) {
            onSearch(newFilters, sortOrder);
        }
    };

    const handleSortChange = (value) => {
        setSortOrder(value);
        if (onSearch) {
            onSearch(filters, value);
        }
    };

    const clearFilter = (key) => {
        handleFilterChange(key, 'all');
    };

    const clearAllFilters = () => {
        const resetFilters = { zone: 'all', type: 'all', category: 'all', bedrooms: 'all' };
        setFilters(resetFilters);
        setSortOrder('default');
        if (onSearch) {
            onSearch(resetFilters, 'default');
        }
    };

    const activeFiltersCount = Object.values(filters).filter(v => v !== 'all').length;

    return (
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg -mt-8 sm:-mt-16 relative z-20 mx-4 md:mx-auto max-w-6xl border border-gray-100">
            {/* Main Filters Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-4">
                <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Ubicación</label>
                    <Select value={filters.zone} onValueChange={(v) => handleFilterChange('zone', v)}>
                        <SelectTrigger className="w-full border-gray-200 bg-gray-50 focus:bg-white">
                            <MapPin className="w-4 h-4 mr-2 text-secondary" />
                            <SelectValue placeholder="Todas las zonas" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todas las zonas</SelectItem>
                            {options.zones.map(zone => <SelectItem key={zone} value={zone}>{zone}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Operación</label>
                    <Select value={filters.type} onValueChange={(v) => handleFilterChange('type', v)}>
                        <SelectTrigger className="w-full border-gray-200 bg-gray-50 focus:bg-white">
                            <DollarSign className="w-4 h-4 mr-2 text-secondary" />
                            <SelectValue placeholder="Venta / Alquiler" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            {options.types.map(type => <SelectItem key={type} value={type}>{type}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Tipo</label>
                    <Select value={filters.category} onValueChange={(v) => handleFilterChange('category', v)}>
                        <SelectTrigger className="w-full border-gray-200 bg-gray-50 focus:bg-white">
                            <Home className="w-4 h-4 mr-2 text-secondary" />
                            <SelectValue placeholder="Todos los tipos" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos los tipos</SelectItem>
                            {options.categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Habitaciones</label>
                    <Select value={filters.bedrooms} onValueChange={(v) => handleFilterChange('bedrooms', v)}>
                        <SelectTrigger className="w-full border-gray-200 bg-gray-50 focus:bg-white">
                            <BedDouble className="w-4 h-4 mr-2 text-secondary" />
                            <SelectValue placeholder="Cualquiera" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Cualquiera</SelectItem>
                            {options.bedrooms.map(num => (
                                <SelectItem key={num} value={num.toString()}>{num === 0 ? 'Sin habitación' : `${num}+ Habitaciones`}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex items-end sm:col-span-2 lg:col-span-1">
                    <Button className="w-full bg-secondary hover:bg-secondary/90 text-white font-bold h-10 sm:h-10" onClick={() => onSearch && onSearch(filters, sortOrder)}>
                        <Search className="w-4 h-4 mr-2" />
                        Buscar
                    </Button>
                </div>
            </div>

            {/* Active Filters Chips & Sorting */}
            {(activeFiltersCount > 0 || sortOrder !== 'default') && (
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center pt-4 border-t border-gray-100 gap-4">
                    <div className="flex flex-wrap gap-2 items-center">
                        <span className="text-xs text-gray-500 font-medium mr-2">Filtros activos:</span>

                        {filters.zone !== 'all' && (
                            <Badge variant="secondary" className="bg-blue-50 text-blue-700 hover:bg-blue-100 gap-1 pl-2 pr-1 py-1">
                                {filters.zone} <X size={14} className="cursor-pointer" onClick={() => clearFilter('zone')} />
                            </Badge>
                        )}
                        {filters.type !== 'all' && (
                            <Badge variant="secondary" className="bg-green-50 text-green-700 hover:bg-green-100 gap-1 pl-2 pr-1 py-1">
                                {filters.type} <X size={14} className="cursor-pointer" onClick={() => clearFilter('type')} />
                            </Badge>
                        )}
                        {filters.category !== 'all' && (
                            <Badge variant="secondary" className="bg-purple-50 text-purple-700 hover:bg-purple-100 gap-1 pl-2 pr-1 py-1">
                                {filters.category} <X size={14} className="cursor-pointer" onClick={() => clearFilter('category')} />
                            </Badge>
                        )}
                        {filters.bedrooms !== 'all' && (
                            <Badge variant="secondary" className="bg-orange-50 text-orange-700 hover:bg-orange-100 gap-1 pl-2 pr-1 py-1">
                                {filters.bedrooms}+ Hab. <X size={14} className="cursor-pointer" onClick={() => clearFilter('bedrooms')} />
                            </Badge>
                        )}

                        <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 h-7">
                            Limpiar todo
                        </Button>
                    </div>

                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <ArrowDownUp size={16} className="text-gray-400" />
                        <Select value={sortOrder} onValueChange={handleSortChange}>
                            <SelectTrigger className="w-[180px] border-none bg-transparent shadow-none text-sm font-medium text-gray-600 hover:text-primary p-0 h-auto">
                                <SelectValue placeholder="Ordenar por" />
                            </SelectTrigger>
                            <SelectContent align="end">
                                <SelectItem value="default">Relevancia</SelectItem>
                                <SelectItem value="price-asc">Precio: Menor a Mayor</SelectItem>
                                <SelectItem value="price-desc">Precio: Mayor a Menor</SelectItem>
                                <SelectItem value="recent">Más Recientes</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuickSearch;
